import { useState } from 'react'
import {
  useBambooIntrospect,
  type BambooMetaField,
  type BambooMetaList,
} from '@/common/hooks/useBambooIntrospect'
import { Table } from '@/common/components/table'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { Text } from '@/common/components/text'
import { Stack } from '@/common/components/stack'
import { Badge } from '@/common/components/badge'
import { colors } from '@/core/styles/tokens'
import * as styles from './BambooIntrospectPage.css'

const DISTINCT_FIELD_LABELS: Record<string, string> = {
  status: 'Status',
  employmentHistoryStatus: 'Employment History Status',
  division: 'Division',
  department: 'Department',
  location: 'Location',
}

function FieldsTable({ fields }: { fields: BambooMetaField[] }) {
  if (fields.length === 0) {
    return (
      <Text size="sm" color="muted">
        No fields to show.
      </Text>
    )
  }

  return (
    <Table.Root size="sm" stickyHeader>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Alias</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>ID</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Relevant</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {fields.map((field, i) => (
          <Table.Row key={`${field.id ?? ''}-${field.alias ?? ''}-${i}`}>
            <Table.Cell>{field.name ?? '—'}</Table.Cell>
            <Table.Cell className={styles.monospace}>{field.alias ?? '—'}</Table.Cell>
            <Table.Cell className={styles.monospace}>{field.id ?? '—'}</Table.Cell>
            <Table.Cell>{field.type ?? '—'}</Table.Cell>
            <Table.Cell>
              {field.relevant ? (
                <Badge color="success" size="sm">
                  Relevant
                </Badge>
              ) : (
                <Badge color="neutral" size="sm">
                  —
                </Badge>
              )}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
}

function ListsTable({ lists }: { lists: BambooMetaList[] }) {
  if (lists.length === 0) {
    return (
      <Text size="sm" color="muted">
        No lists to show.
      </Text>
    )
  }

  return (
    <Table.Root size="sm" stickyHeader>
      <Table.Header>
        <Table.Row>
          <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Alias</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Options</Table.ColumnHeaderCell>
          <Table.ColumnHeaderCell>Relevant</Table.ColumnHeaderCell>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {lists.map((list, i) => (
          <Table.Row key={`${list.alias ?? ''}-${list.name ?? ''}-${i}`}>
            <Table.Cell>{list.name ?? '—'}</Table.Cell>
            <Table.Cell className={styles.monospace}>{list.alias ?? '—'}</Table.Cell>
            <Table.Cell className={styles.optionsCell}>
              {list.options.length > 0 ? list.options.join(', ') : '—'}
            </Table.Cell>
            <Table.Cell>
              {list.relevant ? (
                <Badge color="success" size="sm">
                  Relevant
                </Badge>
              ) : (
                <Badge color="neutral" size="sm">
                  —
                </Badge>
              )}
            </Table.Cell>
          </Table.Row>
        ))}
      </Table.Body>
    </Table.Root>
  )
}

export function BambooIntrospectPage() {
  const { data, error, isFetching, refetch } = useBambooIntrospect()
  const [showAllFields, setShowAllFields] = useState(false)
  const [showAllLists, setShowAllLists] = useState(false)

  const visibleFields = data
    ? showAllFields
      ? data.meta_fields
      : data.meta_fields.filter((f) => f.relevant)
    : []
  const visibleLists = data
    ? showAllLists
      ? data.meta_lists
      : data.meta_lists.filter((l) => l.relevant)
    : []

  const relevantFieldCount = data?.meta_fields.filter((f) => f.relevant).length ?? 0
  const relevantListCount = data?.meta_lists.filter((l) => l.relevant).length ?? 0

  const distinctEntries = data ? Object.entries(data.distinct_values) : []

  return (
    <div className={styles.page}>
      <Stack gap="16">
        <Text size="xl" weight="bold">
          BambooHR Introspection
        </Text>
        <Text size="sm" style={{ color: colors.gray70 }}>
          Reads BambooHR metadata using the app's configured credentials. Use this to inspect the
          available fields, lists, time-off types, and the distinct company/division/status values in
          use. Each run hits the live BambooHR API.
        </Text>

        <button
          className={styles.runButton}
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          {isFetching ? 'Running introspection...' : 'Run introspection'}
        </button>

        {error && <div className={styles.errorBox}>{(error as Error).message}</div>}

        {isFetching && <LoadingSpinner label="Querying BambooHR..." />}

        {data && !isFetching && (
          <Stack gap="32">
            {data.is_sandbox && (
              <div className={styles.sandboxBanner}>
                <strong>Sandbox environment.</strong> Connected to the sandbox subdomain{' '}
                <span className={styles.monospace}>{data.subdomain}</span>. Values shown here are not
                production data.
              </div>
            )}

            {data.warnings.length > 0 && (
              <div className={styles.warningBanner}>
                <strong>Some sections could not be loaded.</strong> The rest of the results below are
                still complete.
                <ul className={styles.warningList}>
                  {data.warnings.map((warning, i) => (
                    <li key={i}>{warning}</li>
                  ))}
                </ul>
              </div>
            )}

            <Stack gap="8" direction="row" wrap>
              <Text size="sm" color="muted">
                Subdomain: <span className={styles.monospace}>{data.subdomain}</span>
              </Text>
              <Text size="sm" color="muted">
                · Employees sampled: {data.employees_returned}
              </Text>
            </Stack>

            {/* Employment-relevant fields */}
            <Stack gap="12" className={styles.section}>
              <div className={styles.sectionHeader}>
                <Text size="lg" weight="bold">
                  Fields ({showAllFields ? data.meta_fields.length : relevantFieldCount}
                  {showAllFields ? ' total' : ' relevant'})
                </Text>
                <button
                  className={styles.toggleButton}
                  onClick={() => setShowAllFields((v) => !v)}
                >
                  {showAllFields
                    ? `Show relevant only (${relevantFieldCount})`
                    : `Show all (${data.meta_fields.length})`}
                </button>
              </div>
              <FieldsTable fields={visibleFields} />
            </Stack>

            {/* List options */}
            <Stack gap="12" className={styles.section}>
              <div className={styles.sectionHeader}>
                <Text size="lg" weight="bold">
                  Lists ({showAllLists ? data.meta_lists.length : relevantListCount}
                  {showAllLists ? ' total' : ' relevant'})
                </Text>
                <button
                  className={styles.toggleButton}
                  onClick={() => setShowAllLists((v) => !v)}
                >
                  {showAllLists
                    ? `Show relevant only (${relevantListCount})`
                    : `Show all (${data.meta_lists.length})`}
                </button>
              </div>
              <ListsTable lists={visibleLists} />
            </Stack>

            {/* Time-off types */}
            <Stack gap="12" className={styles.section}>
              <Text size="lg" weight="bold">
                Time-off types ({data.time_off_types.length})
              </Text>
              {data.time_off_types.length > 0 ? (
                <Table.Root size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell>ID</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {data.time_off_types.map((type, i) => (
                      <Table.Row key={`${type.id ?? ''}-${i}`}>
                        <Table.Cell className={styles.monospace}>{type.id ?? '—'}</Table.Cell>
                        <Table.Cell>{type.name ?? '—'}</Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              ) : (
                <Text size="sm" color="muted">
                  No time-off types returned.
                </Text>
              )}
            </Stack>

            {/* Distinct values in use */}
            <Stack gap="12" className={styles.section}>
              <Text size="lg" weight="bold">
                Distinct values in use
              </Text>
              <Text size="sm" color="muted">
                Sampled from {data.employees_returned} employee
                {data.employees_returned !== 1 ? 's' : ''}.
              </Text>
              <div className={styles.distinctGrid}>
                {distinctEntries.map(([field, values]) => (
                  <Stack key={field} gap="8">
                    <Text size="sm" weight="bold">
                      {DISTINCT_FIELD_LABELS[field] ?? field}
                    </Text>
                    {values.length > 0 ? (
                      <Table.Root size="sm">
                        <Table.Header>
                          <Table.Row>
                            <Table.ColumnHeaderCell>Value</Table.ColumnHeaderCell>
                            <Table.ColumnHeaderCell align="right">Count</Table.ColumnHeaderCell>
                          </Table.Row>
                        </Table.Header>
                        <Table.Body>
                          {values.map((v, i) => (
                            <Table.Row key={`${v.value}-${i}`}>
                              <Table.Cell>{v.value}</Table.Cell>
                              <Table.Cell align="right" className={styles.countCell}>
                                {v.count}
                              </Table.Cell>
                            </Table.Row>
                          ))}
                        </Table.Body>
                      </Table.Root>
                    ) : (
                      <Text size="sm" color="muted">
                        No values.
                      </Text>
                    )}
                  </Stack>
                ))}
              </div>
            </Stack>
          </Stack>
        )}
      </Stack>
    </div>
  )
}
